'use client';
import { UserRound } from 'lucide-react';
import { FaQuoteLeft } from 'react-icons/fa';

export interface TestimonialItem {
  id: number;
  title?: string;
  description?: string;
  quote?: string;
  author: string;
  role?: string;
  rating?: number;
}

interface TProps {
  heading?: string;
  subHeading?: string;
  testimonials?: TestimonialItem[];
}

const TestimonialCard = ({ item }: { item: TestimonialItem }) => {
  const isRich = Boolean(item.title || item.description);

  return (
    <div className='flex flex-col w-80 md:w-[32rem] shrink-0 overflow-hidden rounded-3xl border border-gray-200 bg-slate-50 p-6 md:p-8 gap-4'>
      {isRich ? (
        <>
          <div className='flex items-start gap-3'>
            <FaQuoteLeft className='text-primary mt-1 h-5 w-5 shrink-0' />
            <p className='text-base md:text-lg font-bold text-gray-900 leading-snug'>
              {item.title}
            </p>
          </div>
          <p className='text-sm md:text-base text-gray-600 leading-relaxed flex-1'>
            {item.description}
          </p>
        </>
      ) : (
        <>
          <FaQuoteLeft className='text-primary h-8 w-8 md:h-10 md:w-10' />
          <p className='flex-1 text-base md:text-xl leading-relaxed font-medium text-gray-800 line-clamp-4'>
            &ldquo;{item.quote}&rdquo;
          </p>
        </>
      )}

      <div className='mt-auto flex items-center gap-3 border-t border-gray-200 pt-4'>
        <div className='flex items-center justify-center rounded-full bg-primary/10 p-2 shrink-0'>
          <UserRound className='h-5 w-5 text-primary' />
        </div>
        <div className='flex flex-col min-w-0'>
          <p className='text-sm md:text-base font-semibold text-gray-900 truncate'>
            {item.author}
          </p>
          {item.role && (
            <p className='text-xs text-gray-500 truncate'>{item.role}</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Testimonials = ({
  heading = "What Organisers Say About Greet",
  subHeading = "Real feedback from the teams who trust us with their events.",
  testimonials = [],
}: TProps) => {
  const scrollItems = [...testimonials, ...testimonials];

  return (
    <section className='bg-white py-16 lg:py-24'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-16 text-center'>
          <h2 className='text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl leading-11 sm:leading-12 md:leading-16'>
            {heading}
          </h2>
          <p className='mt-2 text-base font-semibold text-gray-500'>
            {subHeading}
          </p>
        </div>
      </div>

      <div className='relative w-full overflow-hidden'>
        <div className='animate-infinite-scroll flex w-max gap-6 hover:[animation-play-state:paused] py-4 px-4'>
          {scrollItems.map((item, index) => (
            <TestimonialCard key={index} item={item} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
